import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { Auth0DecodedHash, WebAuth } from "auth0-js";
import SecureLS from "secure-ls";
import { useStorage } from "@vueuse/core";
import { useAuthState } from "../composables/useAuthState";

interface MfaState {
  isMFACompleted: boolean;
  mfaRetriggerLimit: number | null;
}

const ls = new SecureLS({
  encodingType: "rc4",
  isCompression: false,
  encryptionSecret: import.meta.env.RAM_STORAGE_SECRET,
});

export const useAuthStore = defineStore("authStore", () => {
  const auth0 = new WebAuth({
    domain: import.meta.env.AUTH0_DOMAIN,
    clientID: import.meta.env.AUTH0_CLIENT_ID,
    redirectUri: `${window.location.origin}/auth0callback`,
    responseType: "token id_token",
    scope: "openid profile email",
  });
  const { authState, setAuthState } = useAuthState();
  const mfaState = useStorage<MfaState>("mfaState", {
    isMFACompleted: false,
    mfaRetriggerLimit: null,
  });
  const milliseconds = 1000;
  const authResult = ref<Auth0DecodedHash>();

  const isAuthenticated = computed(() => {
    if (
      authState.value.accessToken &&
      authState.value.idToken &&
      Math.round(Date.now() / milliseconds) < authState.value.expiresAt
    ) {
      return true;
    }
    return false;
  });

  function login() {
    auth0.authorize();
  }

  function refreshLogin() {
    localStorage.removeItem("authState");
    login();
  }

  function logout(reason = "logout") {
    localStorage.removeItem("authState");
    localStorage.removeItem("mfaState");

    const returnUrl = encodeURIComponent(
      `${window.location.origin}/auth?reason=${reason}`,
    );
    const logoutUrl = `https://${import.meta.env.AUTH0_DOMAIN}/v2/logout?returnTo=${returnUrl}&client_id=${import.meta.env.AUTH0_CLIENT_ID}`;
    window.location.href = logoutUrl;
    return logoutUrl;
  }

  function handleAuth0Callback() {
    return new Promise((resolve, reject) => {
      auth0.parseHash((err, authRes) => {
        if (err) {
          logout();
          reject(false);
        }

        authResult.value = authRes!;
        setAuthState({
          user: {
            email: authRes?.idTokenPayload.email,
            name: authRes?.idTokenPayload.name,
            nickname: authRes?.idTokenPayload.nickname,
            picture: authRes?.idTokenPayload.picture,
          },
          accessToken: authRes?.accessToken!,
          idToken: authRes?.idToken!,
          expiresAt: authRes?.idTokenPayload.exp,
          issuedAt: authRes?.idTokenPayload.iat,
        });

        resolve(true);
      });
    });
  }

  function setMFAComplete() {
    mfaState.value.isMFACompleted = true;

    // Backwards Compatibility for Old Unify
    ls.set("user", JSON.stringify(authResult.value?.idTokenPayload));
    ls.set("access_token", authResult.value?.accessToken);
    ls.set("id_token", authResult.value?.idToken);
    ls.set("token_data", authResult.value);
    ls.set("start_at", authResult.value?.idTokenPayload.iat);
    ls.set("expires_at", authResult.value?.idTokenPayload.exp);
    ls.set("last_activity_at", Math.round(new Date().valueOf() / 1000));
  }

  function setMfaRetriggerLimit(limit: number) {
    mfaState.value.mfaRetriggerLimit = limit;
  }

  return {
    authState,
    mfaState,
    isAuthenticated,
    login,
    refreshLogin,
    logout,
    handleAuth0Callback,
    setMFAComplete,
    setMfaRetriggerLimit,
  };
});
